import isEmpty from "lodash/isEmpty";
import { isAudit, isEngagement } from "PFApp/activities/helpers";
import { ProfileSource } from "PFApp/components/profile_source_toggle";
import { Activity, Booking, Template } from "PFTypes";

type GetAllowedTargetsWithData = {
  bookings: Omit<Booking, "activity">[];
  activities: Activity[];
  findTemplate: (attributesToCheck: Partial<Template>) => Template | undefined;
  canReassignBookingsMatches: boolean;
  canReassignBookingsNamedResource: boolean;
};

export type DisallowReasons = {
  differentAuditRoles: boolean;
  notSingleAuditRoleRelated: boolean;
};

type GetAllowedTargetsWithDataReturn = {
  allowedTargets: ProfileSource[];
  allowedBookings: Omit<Booking, "activity">[];
  allowedActivities: Activity[];
  disallowReasons: DisallowReasons;
};

type GetAuditConditionsParams = {
  activities: Activity[];
  findTemplate: (attributesToCheck: Partial<Template>) => Template | undefined;
  areAllSingleRoleRelated: boolean;
};

const getAuditConditions = ({
  activities,
  findTemplate,
  areAllSingleRoleRelated
}: GetAuditConditionsParams) => {
  const areAllSingleAuditRoleRelated =
    areAllSingleRoleRelated &&
    activities.some((activity) => {
      const template = findTemplate({ id: activity.template_id });
      return isAudit(template);
    });

  const noAuditActivities = activities.every(
    (activity) => !isAudit(findTemplate({ id: activity.template_id }))
  );

  const areDifferentAuditRolesRelated =
    activities.filter((activity) => {
      const template = findTemplate({ id: activity.template_id });
      return isAudit(template);
    }).length > 1;

  const allowedAuditCondition =
    !areDifferentAuditRolesRelated && (noAuditActivities || areAllSingleAuditRoleRelated);

  return {
    allowedAuditCondition,
    differentAuditRoles: areDifferentAuditRolesRelated,
    notSingleAuditRoleRelated: !areAllSingleAuditRoleRelated
  };
};

export const getAllowedTargetsWithData = ({
  bookings,
  activities,
  findTemplate,
  canReassignBookingsMatches,
  canReassignBookingsNamedResource
}: GetAllowedTargetsWithData): GetAllowedTargetsWithDataReturn => {
  if (isEmpty(bookings)) {
    return {
      allowedTargets: [],
      allowedBookings: [],
      allowedActivities: [],
      disallowReasons: {
        differentAuditRoles: false,
        notSingleAuditRoleRelated: false
      }
    };
  }

  const areNonDemandBookingsSelected = bookings.some(({ activity_id: activityId }) => !activityId);

  const areEngagementBookingsSelected = activities.some((activity) => {
    const template = findTemplate({ id: activity.template_id });
    return isEngagement(template);
  });

  const areAllSingleRoleRelated =
    activities.length === 1 && !areNonDemandBookingsSelected && !areEngagementBookingsSelected;
  const isActivityCoowned = activities.length === 1 && activities[0].coowned;

  const { allowedAuditCondition, differentAuditRoles, notSingleAuditRoleRelated } = getAuditConditions({
    activities,
    findTemplate,
    areAllSingleRoleRelated
  });

  const allowedMatchesCondition =
    allowedAuditCondition && canReassignBookingsMatches && areAllSingleRoleRelated && isActivityCoowned;
  const allowedNamedResourceCondition = allowedAuditCondition && canReassignBookingsNamedResource;

  const allowedTargets = [
    ...(allowedMatchesCondition ? [ProfileSource.Matches] : []),
    ...(allowedNamedResourceCondition ? [ProfileSource.NamedResource] : [])
  ];

  return {
    allowedTargets,
    allowedBookings: bookings,
    allowedActivities: activities,
    disallowReasons: {
      differentAuditRoles,
      notSingleAuditRoleRelated
    }
  };
};
